import React from 'react';

/**
 * HeroIllustration — custom SVG of an Irish row of houses, designed
 * to match "So close to home." Friendly, hand-illustrated, brand-purple
 * accents, with the centre house's door open and warm light spilling out.
 * Replaces the cold abstract data-viz image previously in the hero.
 */
const HeroIllustration = ({ className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 640 540"
    className={className}
    role="img"
    aria-label="Illustrated row of Irish houses, one with its door open and warm light spilling out"
    style={{ display: 'block', width: '100%', height: 'auto', overflow: 'visible' }}
  >
    <defs>
      <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#F5EFFF" />
        <stop offset="100%" stopColor="#FFF7F0" />
      </linearGradient>
      <radialGradient id="warmlight" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0%" stopColor="#FFE9B0" stopOpacity="0.95" />
        <stop offset="60%" stopColor="#FFD083" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#FFD083" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="window-glow" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FFE3A0" />
        <stop offset="100%" stopColor="#F8C76B" />
      </linearGradient>
      <pattern id="dots" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
        <circle cx="2" cy="2" r="1.2" fill="#7042cc" opacity="0.18" />
      </pattern>
    </defs>

    {/* Soft sky background */}
    <rect x="0" y="0" width="640" height="540" fill="url(#sky)" />

    {/* Sun / moon — soft light source */}
    <circle cx="540" cy="120" r="58" fill="#FFE9B0" opacity="0.6" />
    <circle cx="540" cy="120" r="40" fill="#FFD083" opacity="0.7" />

    {/* Distant trees — abstract, soft */}
    <g opacity="0.55">
      <ellipse cx="80" cy="380" rx="42" ry="56" fill="#C8B6E2" />
      <ellipse cx="160" cy="370" rx="32" ry="44" fill="#B7A3D6" />
      <ellipse cx="500" cy="370" rx="38" ry="50" fill="#C8B6E2" />
      <ellipse cx="580" cy="380" rx="34" ry="46" fill="#B7A3D6" />
    </g>

    {/* Dotted ground texture */}
    <rect x="0" y="450" width="640" height="90" fill="url(#dots)" />
    <line x1="0" y1="450" x2="640" y2="450" stroke="#7042cc" strokeWidth="1.6" opacity="0.35" />

    {/* ==================== ROW OF HOUSES (4) ==================== */}

    {/* House 1 — cream, blue door */}
    <g>
      <polygon points="60,260 200,260 180,200 80,200" fill="#5947C2" opacity="0.18" />
      <rect x="70" y="260" width="120" height="190" fill="#FFF1E0" stroke="#7042cc" strokeWidth="2.2" />
      <rect x="68" y="448" width="124" height="6" fill="#7042cc" opacity="0.7" />
      <rect x="115" y="358" width="38" height="92" fill="#3B5BB8" stroke="#7042cc" strokeWidth="2" />
      <circle cx="146" cy="404" r="2" fill="#FFC857" />
      <rect x="84" y="280" width="34" height="34" fill="#fff" stroke="#7042cc" strokeWidth="2" />
      <line x1="101" y1="280" x2="101" y2="314" stroke="#7042cc" strokeWidth="1.5" />
      <line x1="84" y1="297" x2="118" y2="297" stroke="#7042cc" strokeWidth="1.5" />
      <rect x="146" y="280" width="34" height="34" fill="url(#window-glow)" stroke="#7042cc" strokeWidth="2" />
      <line x1="163" y1="280" x2="163" y2="314" stroke="#7042cc" strokeWidth="1.5" />
      <line x1="146" y1="297" x2="180" y2="297" stroke="#7042cc" strokeWidth="1.5" />
    </g>

    {/* House 2 — terracotta, mustard door */}
    <g>
      <polygon points="200,250 320,250 300,190 220,190" fill="#5947C2" opacity="0.22" />
      <rect x="210" y="250" width="100" height="200" fill="#FBD7BE" stroke="#7042cc" strokeWidth="2.2" />
      <rect x="208" y="448" width="104" height="6" fill="#7042cc" opacity="0.7" />
      <rect x="245" y="358" width="32" height="92" fill="#E2A93D" stroke="#7042cc" strokeWidth="2" />
      <circle cx="270" cy="404" r="2" fill="#5947C2" />
      <rect x="222" y="270" width="30" height="30" fill="url(#window-glow)" stroke="#7042cc" strokeWidth="2" />
      <line x1="237" y1="270" x2="237" y2="300" stroke="#7042cc" strokeWidth="1.4" />
      <line x1="222" y1="285" x2="252" y2="285" stroke="#7042cc" strokeWidth="1.4" />
      <rect x="268" y="270" width="30" height="30" fill="#fff" stroke="#7042cc" strokeWidth="2" />
      <line x1="283" y1="270" x2="283" y2="300" stroke="#7042cc" strokeWidth="1.4" />
      <line x1="268" y1="285" x2="298" y2="285" stroke="#7042cc" strokeWidth="1.4" />
    </g>

    {/* House 3 — CENTRE, brand purple, OPEN DOOR with warm light */}
    <g>
      <polygon points="320,235 460,235 440,170 340,170" fill="#7042cc" opacity="0.92" />
      <rect x="330" y="235" width="120" height="215" fill="#EDE3FB" stroke="#7042cc" strokeWidth="2.6" />
      <rect x="328" y="448" width="124" height="6" fill="#7042cc" />
      {/* Warm light pool spilling onto the path */}
      <ellipse cx="389" cy="475" rx="78" ry="20" fill="url(#warmlight)" />
      {/* Open door — door panel swung outward */}
      <rect x="368" y="338" width="42" height="112" fill="#FFE3A0" stroke="#7042cc" strokeWidth="2" />
      <polygon points="410,338 446,322 446,452 410,450" fill="#7042cc" />
      <polygon points="410,338 446,322 446,330 410,346" fill="#5b35a8" />
      {/* Welcome mat */}
      <rect x="362" y="447" width="58" height="8" fill="#7042cc" opacity="0.65" />
      <line x1="362" y1="451" x2="420" y2="451" stroke="#FFE3A0" strokeWidth="1.4" />
      {/* Windows — both lit warmly */}
      <rect x="345" y="258" width="34" height="34" fill="url(#window-glow)" stroke="#7042cc" strokeWidth="2" />
      <line x1="362" y1="258" x2="362" y2="292" stroke="#7042cc" strokeWidth="1.4" />
      <line x1="345" y1="275" x2="379" y2="275" stroke="#7042cc" strokeWidth="1.4" />
      <rect x="403" y="258" width="34" height="34" fill="url(#window-glow)" stroke="#7042cc" strokeWidth="2" />
      <line x1="420" y1="258" x2="420" y2="292" stroke="#7042cc" strokeWidth="1.4" />
      <line x1="403" y1="275" x2="437" y2="275" stroke="#7042cc" strokeWidth="1.4" />
      {/* Tiny floating heart above centre house */}
      <g transform="translate(376 130)">
        <path
          d="M14 30 C 0 20, -3 8, 6 3 C 12 0, 16 4, 14 9 C 12 4, 16 0, 22 3 C 31 8, 28 20, 14 30 Z"
          fill="#7042cc"
        />
      </g>
    </g>

    {/* House 4 — mint, navy door */}
    <g>
      <polygon points="460,260 580,260 560,200 480,200" fill="#5947C2" opacity="0.18" />
      <rect x="470" y="260" width="100" height="190" fill="#DCF1E5" stroke="#7042cc" strokeWidth="2.2" />
      <rect x="468" y="448" width="104" height="6" fill="#7042cc" opacity="0.7" />
      <rect x="503" y="358" width="32" height="92" fill="#1F3A6B" stroke="#7042cc" strokeWidth="2" />
      <circle cx="528" cy="404" r="2" fill="#FFC857" />
      <rect x="482" y="280" width="30" height="30" fill="#fff" stroke="#7042cc" strokeWidth="2" />
      <line x1="497" y1="280" x2="497" y2="310" stroke="#7042cc" strokeWidth="1.4" />
      <line x1="482" y1="295" x2="512" y2="295" stroke="#7042cc" strokeWidth="1.4" />
      <rect x="526" y="280" width="30" height="30" fill="url(#window-glow)" stroke="#7042cc" strokeWidth="2" />
      <line x1="541" y1="280" x2="541" y2="310" stroke="#7042cc" strokeWidth="1.4" />
      <line x1="526" y1="295" x2="556" y2="295" stroke="#7042cc" strokeWidth="1.4" />
    </g>

    {/* Path leading to the open door */}
    <path
      d="M388 460 Q 360 490, 300 520 M388 460 Q 416 490, 478 520"
      fill="none"
      stroke="#7042cc"
      strokeWidth="2"
      opacity="0.4"
      strokeDasharray="2 6"
    />
  </svg>
);

export default HeroIllustration;
